import { useDebouncedEffect } from '@react-hookz/web';
import { ClipboardEvent, KeyboardEvent, SyntheticEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { company } from '~/company/Company';
import { mainStore } from '~/stores';
import { createMasked } from '~/utils/inputMaskHelpers';
import {
  validateCountryCodePlus,
  validatePhoneValue,
} from '~/utils/inputPhoneHelpers';

export const usePhoneInput = ({
  onChange,
  value,
  onValidate,
}: {
  value: string;
  onChange?: (value: string) => void;
  onValidate?: (isValid: boolean) => void;
}) => {
  const { t } = useTranslation();
  const [isValid, setIsValid] = useState(false);
  const [validationError, setValidationError] = useState('');

  const isDevEnv = mainStore.environment !== 'production';

  const handleChange = (e: SyntheticEvent) => {
    if (!onChange) {
      return;
    }

    const target = e.target as HTMLInputElement;
    const value = target.value.replace(/[^0-9+]/g, '');

    onChange(validateCountryCodePlus(value));
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    const ASCIICode = e.key.charCodeAt(0);

    if (!validatePhoneValue(ASCIICode, target.value)) {
      e.preventDefault();
      return false;
    }
  };

  const handlePaste = (e: ClipboardEvent) => {
    const clipboardData = e.clipboardData.getData('text/plain');

    e.preventDefault();

    const formatData = clipboardData.replace(/[\s+]/gi, '');
    const value = Array.from(formatData)
      .filter((letter) => {
        const ASCIICode = letter.charCodeAt(0);
        return validatePhoneValue(ASCIICode, formatData);
      })
      .join('');
    onChange?.(validateCountryCodePlus(value));
  };

  useDebouncedEffect(
    () => {
      if (!value) {
        onValidate?.(false);
        setIsValid(false);
        setValidationError(
          company.newDesignSystemEnabled ? t('errors:cantBeEmpty') : '',
        );
        return;
      }

      if (!isDevEnv && (value.startsWith('0') || value.startsWith('+0'))) {
        onValidate?.(false);
        setIsValid(false);
        setValidationError(t('prodPhoneValidationError'));
        return;
      } else {
        setValidationError('');
      }

      const masked = createMasked();

      masked.resolve(value);
      const isValid = masked.isComplete;
      onValidate?.(isValid);
      setIsValid(isValid);

      if (!isValid && company.newDesignSystemEnabled) {
        setValidationError(t('prodPhoneValidationError'));
      }
    },
    [value],
    200,
  );

  return {
    isValid,
    validationError,
    handleChange,
    handleKeyPress,
    handlePaste,
  };
};
